import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useShowPoputMutation } from '@/api/queries/shop';
import { AppRoute } from '@/common/app/routes';
import { type ArenaOffersDto } from '@/common/types/shop/marketBoxes';
import { ModalContext } from '@/components/Modals/Modal/config';
import { StringMain } from '@/components/Typography/Typography';
import { Close } from '@/components/ui/Close/Close';
import { showPopup } from '@/redux/reducers/shopReducer';
import { selectAvailableProfitDrops } from '@/redux/selectors/shopSelector';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import PopupCard from './PopupCard';

interface ArenaOfferPopupProps {
  offer?: ArenaOffersDto;
  type?: 'profitDrop' | 'arena';
}

const ArenaOfferPopup: React.FC<ArenaOfferPopupProps> = ({ offer, type }) => {
  const arenaOffers = useAppSelector((state) => state.shop.arenaOffers);
  const profitDropOffers = useAppSelector(selectAvailableProfitDrops);
  const { t } = useTranslation();
  const { closeModal, secondModal } = useContext(ModalContext);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [arenaShownPopup] = useShowPoputMutation();
  const onClick = async (nav: boolean): Promise<void> => {
    if (arenaOffers[0]) {
      await arenaShownPopup({
        leagueId: arenaOffers[0].leagueId,
        type: arenaOffers[0].type,
      });
    }
    closeModal();
    secondModal.closeModal();
    dispatch(showPopup(false));

    if (nav) navigate(AppRoute.SHOP);
  };

  const sorted = [...profitDropOffers, ...arenaOffers]
    .sort((a, b) => a.cost.base.usd - b.cost.base.usd)
    .filter((el) => {
      const isProfitDrop =
        el.name === 'profitDropLarge' || el.name === 'profitDropSmall';

      if (type === 'profitDrop') {
        return isProfitDrop;
      } else if (type === 'arena') {
        return !isProfitDrop;
      }

      return true;
    });

  return (
    <div className="pt-[40%] px-[24px]">
      {offer ? (
        <PopupCard arenaOffer={offer} />
      ) : (
        <Swiper
          spaceBetween={10}
          centeredSlides={true}
          loop={false}
          allowTouchMove={true}
          className="!overflow-visible"
        >
          {sorted.map((el) => {
            return (
              <SwiperSlide key={el.id}>
                <PopupCard arenaOffer={el} />
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}
      <div className="flex flex-col items-center gap-[20px] mt-[20px]">
        <StringMain
          className="text-center !font-[800] !text-[16px]"
          onClick={() => onClick(true)}
        >
          {t('More offers')}
        </StringMain>
        <Close imageVariant="modal" size="lg" onClick={() => onClick(false)} />
      </div>
    </div>
  );
};

export default ArenaOfferPopup;
